import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import DashboardChartCard from '../../../components/common/DashboardChartCard';
import DashboardReportHeader from '../../../components/common/DashboardReportHeader';

const HALL_ACCENT = '#4ce1b6';
const SECONDARY = '#8ac9ff';
const WARNING = '#fbbf24';
const DANGER = '#fb7185';
const PURPLE = '#c4b5fd';
const PROFIT = '#34d399';
const EXPENSE = '#f97316';

const numberFormat = new Intl.NumberFormat('en-IN');

function MetricTile({ title, value, subtitle, icon, color, isDark, formatValue, loading }) {
  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        borderRadius: '18px',
        border: `1px solid ${alpha(color, isDark ? 0.28 : 0.2)}`,
        background: isDark
          ? `linear-gradient(155deg, ${alpha('#111827', 0.96)} 0%, ${alpha('#0f172a', 0.94)} 55%, ${alpha(color, 0.12)} 100%)`
          : `linear-gradient(155deg, #ffffff 0%, #f8fafc 50%, ${alpha(color, 0.12)} 100%)`,
        boxShadow: isDark
          ? `0 12px 28px ${alpha('#000', 0.28)}`
          : `0 14px 30px ${alpha('#0f172a', 0.07)}, 0 0 0 1px ${alpha('#fff', 0.55)} inset`,
        transition: 'transform 0.22s ease, box-shadow 0.22s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: isDark
            ? `0 16px 34px ${alpha('#000', 0.34)}, 0 0 0 1px ${alpha(color, 0.25)}`
            : `0 18px 36px ${alpha(color, 0.16)}`,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -36,
          right: -28,
          width: 110,
          height: 110,
          borderRadius: '50%',
          bgcolor: alpha(color, isDark ? 0.12 : 0.14),
          pointerEvents: 'none',
        }}
      />
      <Stack spacing={1.25} sx={{ p: { xs: 1.75, sm: 2 }, position: 'relative', zIndex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'grid',
              placeItems: 'center',
              color,
              background: `linear-gradient(145deg, ${alpha(color, 0.28)} 0%, ${alpha(color, 0.1)} 100%)`,
              border: `1px solid ${alpha(color, 0.35)}`,
              boxShadow: `0 6px 14px ${alpha(color, 0.2)}`,
              '& .MuiSvgIcon-root': { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
          <Chip
            size="small"
            label="LIVE"
            sx={{
              height: 22,
              fontSize: '0.65rem',
              fontWeight: 800,
              letterSpacing: '0.04em',
              color,
              bgcolor: alpha(color, 0.12),
              border: `1px solid ${alpha(color, 0.25)}`,
            }}
          />
        </Stack>
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: alpha(isDark ? '#e2e8f0' : '#475569', 0.9),
              fontWeight: 800,
              letterSpacing: '0.08em',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              mt: 0.35,
              fontWeight: 900,
              letterSpacing: '-0.03em',
              color: isDark ? '#f8fafc' : '#0f172a',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {loading ? '—' : formatValue(value)}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" sx={{ color: isDark ? alpha('#94a3b8', 0.95) : '#64748b', fontWeight: 600 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

/**
 * Hall Dashboard analytics canvas — KPI tiles + charts (no hall portfolio).
 */
export default function HallAnalyticsOverview({
  isDark,
  tokens,
  themeMode,
  surfaceSx,
  insightPaperSx,
  analytics,
  analyticsChartData,
  analyticsLoading,
  analyticsPdfCaptureMode,
  analyticsPdfExporting,
  analyticsInsightsRef,
  analyticsRangeLabel,
  analyticsStartDate,
  analyticsEndDate,
  analyticsPreset,
  onDateRangeChange,
  onRefresh,
  onPdfDownload,
  formatCurrency,
  upcomingEvents = [],
  statusChipSx,
  renderEventIdLink,
  navigate,
}) {
  const kpis = analytics?.kpis || {};
  const chartHeight = analyticsPdfCaptureMode ? 300 : 250;

  const metricTiles = useMemo(() => ([
    {
      key: 'halls',
      title: 'Total Hall',
      value: kpis.total_halls ?? kpis.available_halls ?? 0,
      subtitle: 'In portfolio',
      color: SECONDARY,
      icon: <MapsHomeWorkOutlinedIcon />,
      formatValue: (v) => numberFormat.format(Number(v || 0)),
    },
    {
      key: 'booked',
      title: 'Total Event Booked',
      value: kpis.total_events_booked ?? kpis.range_bookings ?? 0,
      subtitle: analyticsRangeLabel,
      color: PURPLE,
      icon: <EventNoteOutlinedIcon />,
      formatValue: (v) => numberFormat.format(Number(v || 0)),
    },
    {
      key: 'completed',
      title: 'Total Event Completed',
      value: kpis.total_events_completed ?? 0,
      subtitle: analyticsRangeLabel,
      color: PROFIT,
      icon: <CheckCircleOutlineIcon />,
      formatValue: (v) => numberFormat.format(Number(v || 0)),
    },
    {
      key: 'upcoming',
      title: 'Upcoming Events',
      value: kpis.upcoming_events ?? 0,
      subtitle: 'Scheduled ahead',
      color: HALL_ACCENT,
      icon: <EventAvailableOutlinedIcon />,
      formatValue: (v) => numberFormat.format(Number(v || 0)),
    },
    {
      key: 'cancelled',
      title: 'Cancelled',
      value: kpis.total_cancelled ?? 0,
      subtitle: analyticsRangeLabel,
      color: DANGER,
      icon: <CancelOutlinedIcon />,
      formatValue: (v) => numberFormat.format(Number(v || 0)),
    },
    {
      key: 'revenue',
      title: 'Total Revenue',
      value: kpis.total_revenue ?? kpis.range_revenue ?? 0,
      subtitle: analyticsRangeLabel,
      color: HALL_ACCENT,
      icon: <PaidOutlinedIcon />,
      formatValue: formatCurrency,
    },
    {
      key: 'collection',
      title: 'Total Collection',
      value: kpis.total_collection ?? 0,
      subtitle: 'Received payments',
      color: SECONDARY,
      icon: <AccountBalanceWalletOutlinedIcon />,
      formatValue: formatCurrency,
    },
    {
      key: 'profit',
      title: 'Total Profit',
      value: kpis.total_profit ?? 0,
      subtitle: 'Revenue − expenses',
      color: PROFIT,
      icon: <TrendingUpOutlinedIcon />,
      formatValue: formatCurrency,
    },
    {
      key: 'expenses',
      title: 'Total Expenses',
      value: kpis.total_expenses ?? 0,
      subtitle: analyticsRangeLabel,
      color: EXPENSE,
      icon: <SavingsOutlinedIcon />,
      formatValue: formatCurrency,
    },
    {
      key: 'vendors',
      title: 'Total Vendor',
      value: kpis.total_vendors ?? 0,
      subtitle: 'Active partners',
      color: WARNING,
      icon: <StorefrontOutlinedIcon />,
      formatValue: (v) => numberFormat.format(Number(v || 0)),
    },
  ]), [analyticsRangeLabel, formatCurrency, kpis]);

  const moneyOverviewRows = useMemo(() => {
    const rows = analyticsChartData.money_overview || [];
    if (rows.length) return rows;
    return [
      { name: 'Revenue', label: 'Revenue', value: Number(kpis.total_revenue ?? kpis.range_revenue ?? 0) },
      { name: 'Collection', label: 'Collection', value: Number(kpis.total_collection ?? 0) },
      { name: 'Profit', label: 'Profit', value: Number(kpis.total_profit ?? 0) },
      { name: 'Expenses', label: 'Expenses', value: Number(kpis.total_expenses ?? 0) },
    ];
  }, [analyticsChartData.money_overview, kpis]);

  const moneyPie = useMemo(() => (
    moneyOverviewRows.map((item, index) => ({
      name: item.label || item.name,
      value: Number(item.value || 0),
      color: [HALL_ACCENT, SECONDARY, PROFIT, EXPENSE][index % 4],
    }))
  ), [moneyOverviewRows]);

  const funnelBars = useMemo(() => {
    const rows = analyticsChartData.event_funnel || [];
    if (rows.length) {
      return rows.map((item) => ({ ...item, name: item.name || item.label }));
    }
    return [
      { name: 'Upcoming', label: 'Upcoming', value: Number(kpis.upcoming_events ?? 0) },
      { name: 'Booked', label: 'Booked', value: Number(kpis.total_events_booked ?? kpis.range_bookings ?? 0) },
      { name: 'Completed', label: 'Completed', value: Number(kpis.total_events_completed ?? 0) },
      { name: 'Cancelled', label: 'Cancelled', value: Number(kpis.total_cancelled ?? 0) },
    ];
  }, [analyticsChartData.event_funnel, kpis]);

  return (
    <Paper
      sx={{
        ...insightPaperSx,
        background: isDark
          ? `linear-gradient(145deg, ${alpha('#0b1524', 0.98)} 0%, ${alpha('#0f172a', 0.98)} 100%)`
          : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          spacing={1.5}
        >
          <Box>
            <Typography variant="h6" sx={{ color: tokens.text, fontWeight: 900, letterSpacing: '-0.01em' }}>
              Hall Analytics Command
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.subtleText }}>
              Events, revenue, collections, and vendor health at a glance.
            </Typography>
          </Box>
          <DashboardReportHeader
            actionsOnly
            startDate={analyticsStartDate}
            endDate={analyticsEndDate}
            onDateRangeChange={onDateRangeChange}
            onRefresh={onRefresh}
            onPdfDownload={onPdfDownload}
            loading={analyticsLoading}
            pdfExporting={analyticsPdfExporting}
            pdfDisabled={!analytics?.kpis}
            themeMode={themeMode}
            accentColor={HALL_ACCENT}
            initialPreset={analyticsPreset}
          />
        </Stack>

        <Box
          ref={analyticsInsightsRef}
          sx={{
            p: analyticsPdfCaptureMode ? 3 : 0,
            bgcolor: analyticsPdfCaptureMode
              ? (isDark ? '#0f172a' : '#f8fafc')
              : 'transparent',
            background: analyticsPdfCaptureMode
              ? (isDark
                ? 'linear-gradient(145deg, #0b1524 0%, #0f172a 100%)'
                : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)')
              : 'transparent',
            borderRadius: analyticsPdfCaptureMode ? 3 : 0,
          }}
        >
          {analyticsPdfCaptureMode && (
            <Typography
              variant="h6"
              sx={{
                mb: 2.5,
                fontWeight: 900,
                color: isDark ? '#f8fafc' : '#0f172a',
                letterSpacing: '-0.01em',
              }}
            >
              Hall Analytics — {analyticsRangeLabel}
            </Typography>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                xl: 'repeat(5, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            {metricTiles.map((metric) => (
              <MetricTile
                key={metric.key}
                title={metric.title}
                value={metric.value}
                subtitle={metric.subtitle}
                icon={metric.icon}
                color={metric.color}
                isDark={isDark}
                formatValue={metric.formatValue}
                loading={analyticsLoading && !analyticsPdfCaptureMode}
              />
            ))}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: analyticsPdfCaptureMode
                ? 'repeat(2, minmax(0, 1fr))'
                : { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
              gap: 2.5,
              mt: 2.5,
            }}
          >
            <DashboardChartCard
              variant="performance"
              eyebrow="MONEY PULSE"
              title=""
              subtitle="Revenue · Collection · Profit · Expenses"
              data={moneyOverviewRows}
              chartType="bar"
              height={chartHeight}
              dataKeys={[{ dataKey: 'value', name: 'Amount', color: HALL_ACCENT }]}
              theme={themeMode}
              loading={analyticsLoading && !analyticsPdfCaptureMode}
              formatValue={formatCurrency}
            />
            <DashboardChartCard
              variant="performance"
              eyebrow="EVENT FUNNEL"
              title=""
              subtitle="Upcoming → Booked → Completed → Cancelled"
              data={funnelBars}
              chartType="bar"
              height={chartHeight}
              dataKeys={[{ dataKey: 'value', name: 'Events', color: SECONDARY }]}
              theme={themeMode}
              loading={analyticsLoading && !analyticsPdfCaptureMode}
            />
            <DashboardChartCard
              variant="performance"
              eyebrow="DAILY BOOKING TREND"
              title=""
              subtitle="Bookings by day"
              data={analyticsChartData.daily_booking_trend}
              chartType="line"
              showArea
              height={chartHeight}
              dataKeys={[{ dataKey: 'bookings', name: 'Bookings', color: HALL_ACCENT }]}
              theme={themeMode}
              loading={analyticsLoading && !analyticsPdfCaptureMode}
            />
            <DashboardChartCard
              variant="performance"
              eyebrow="REVENUE TREND"
              title=""
              subtitle="Period revenue curve"
              data={analyticsChartData.monthly_revenue}
              chartType="area"
              height={chartHeight}
              dataKeys={[{ dataKey: 'revenue', name: 'Revenue', color: PROFIT }]}
              theme={themeMode}
              loading={analyticsLoading && !analyticsPdfCaptureMode}
              formatValue={formatCurrency}
            />
            <DashboardChartCard
              variant="performance"
              eyebrow="BOOKING STATUS MIX"
              title=""
              subtitle="Current status distribution"
              chartType="pie"
              pieAsBars
              height={chartHeight}
              pieData={(analyticsChartData.booking_status_distribution || []).map((item, index) => ({
                name: item.label,
                value: item.value,
                color: [HALL_ACCENT, WARNING, SECONDARY, DANGER, PURPLE][index % 5],
              }))}
              theme={themeMode}
              loading={analyticsLoading && !analyticsPdfCaptureMode}
            />
            <DashboardChartCard
              variant="performance"
              eyebrow="FINANCE SHARE"
              title=""
              subtitle="Money composition"
              chartType="pie"
              height={chartHeight}
              pieData={moneyPie}
              theme={themeMode}
              loading={analyticsLoading && !analyticsPdfCaptureMode}
              formatValue={formatCurrency}
            />
            <Box sx={{ gridColumn: { xs: '1', lg: '1 / -1' } }}>
              <DashboardChartCard
                variant="performance"
                eyebrow="MOST POPULAR HALL"
                title=""
                subtitle="Ranked by booking count"
                chartType="pie"
                pieAsBars
                height={analyticsPdfCaptureMode ? 220 : 240}
                pieData={(analyticsChartData.popular_halls || []).map((item, index) => ({
                  name: item.label,
                  value: item.value,
                  color: [HALL_ACCENT, SECONDARY, WARNING, PURPLE][index % 4],
                }))}
                theme={themeMode}
                loading={analyticsLoading && !analyticsPdfCaptureMode}
              />
            </Box>
          </Box>

          {!analyticsPdfCaptureMode && (
            <Paper
              sx={{
                ...surfaceSx,
                mt: 2.5,
                p: { xs: 1.5, md: 2 },
                background: isDark
                  ? `linear-gradient(135deg, ${alpha('#071423', 0.98)} 0%, ${alpha('#081a2a', 0.98)} 60%, ${alpha(HALL_ACCENT, 0.08)} 100%)`
                  : 'linear-gradient(135deg, #ffffff 0%, #f7fbff 100%)',
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: tokens.text, fontWeight: 900 }}>
                      Upcoming Events
                    </Typography>
                    <Typography variant="body2" sx={{ color: tokens.subtleText }}>
                      Next scheduled hall events across your portfolio.
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={`${upcomingEvents.length} Upcoming`}
                    sx={{
                      color: HALL_ACCENT,
                      bgcolor: alpha(HALL_ACCENT, 0.14),
                      border: `1px solid ${alpha(HALL_ACCENT, 0.28)}`,
                      fontWeight: 900,
                    }}
                  />
                </Stack>

                {upcomingEvents.length ? (
                  <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(HALL_ACCENT, 0.14)}` }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(tokens.text, 0.03) }}>
                          <TableCell sx={{ color: tokens.subtleText, fontWeight: 900 }}>EVENT ID</TableCell>
                          <TableCell sx={{ color: tokens.subtleText, fontWeight: 900 }}>EVENT</TableCell>
                          <TableCell sx={{ color: tokens.subtleText, fontWeight: 900 }}>HALL</TableCell>
                          <TableCell sx={{ color: tokens.subtleText, fontWeight: 900 }}>DATE</TableCell>
                          <TableCell sx={{ color: tokens.subtleText, fontWeight: 900 }}>TIME</TableCell>
                          <TableCell sx={{ color: tokens.subtleText, fontWeight: 900 }} align="right">AMOUNT</TableCell>
                          <TableCell sx={{ color: tokens.subtleText, fontWeight: 900 }}>STATUS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {upcomingEvents.map((event) => (
                          <TableRow key={event.id} hover>
                            <TableCell sx={{ color: tokens.text }}>
                              {renderEventIdLink(event, navigate)}
                            </TableCell>
                            <TableCell sx={{ color: tokens.text, fontWeight: 700 }}>
                              <Stack spacing={0.3}>
                                <Typography sx={{ color: tokens.text, fontWeight: 800, fontSize: '0.88rem' }}>
                                  {event.event_name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: tokens.subtleText }}>
                                  {event.customer || 'Guest'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ color: tokens.text }}>{event.hall || '—'}</TableCell>
                            <TableCell sx={{ color: tokens.text }}>{event.date || '—'}</TableCell>
                            <TableCell sx={{ color: tokens.text }}>{event.time || '—'}</TableCell>
                            <TableCell sx={{ color: tokens.text }} align="right">
                              {formatCurrency(event.amount || 0)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={event.status}
                                sx={{ ...statusChipSx(event.status, isDark), textTransform: 'capitalize' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper
                    sx={{
                      ...surfaceSx,
                      py: 3,
                      textAlign: 'center',
                      bgcolor: isDark ? alpha('#020617', 0.34) : alpha('#f8fafc', 0.9),
                    }}
                  >
                    <EventNoteOutlinedIcon sx={{ fontSize: 36, color: alpha(HALL_ACCENT, 0.7), mb: 1 }} />
                    <Typography variant="body1" sx={{ color: tokens.text, fontWeight: 800 }}>
                      No upcoming events
                    </Typography>
                    <Typography variant="body2" sx={{ color: tokens.subtleText, mt: 0.5 }}>
                      New bookings will appear here once scheduled.
                    </Typography>
                  </Paper>
                )}
              </Stack>
            </Paper>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
